import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import YupPassword from "yup-password";

import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Stack,
  Button,
  TextField,
  Typography,
  Grid2 as Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

YupPassword(Yup);

const NotaryRegister = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tokenQuery = queryParams.get("token");
  const firstNameQuery = queryParams.get("first");
  const lastNameQuery = queryParams.get("last");
  const [pageLoading, setPageLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [notaryError, setNotaryError] = useState("");
  const [memorableSelections, setMemorableSelection] = useState([]);
  const [userTimezone, setUserTimeZone] = useState("America/New_York");
  const [storedNotary, setStoredNotary] = useState(null);
  const [showChoiceScreen, setShowChoiceScreen] = useState(true);
  const [skipLoading, setSkipLoading] = useState(false);
  const [showPasswordForSkip, setShowPasswordForSkip] = useState(false);

  let count = 0;

  useEffect(() => {
    if (!tokenQuery || !firstNameQuery || !lastNameQuery) {
      navigate("/login");
      return;
    }
    const validate = async () => {
      try {
        const tokenResponse = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/validate-registration`,
          {
            token: tokenQuery,
            first: firstNameQuery,
            last: lastNameQuery,
            timezone: userTimezone,
          },
          {
            withCredentials: true,
          },
        );
        if (tokenResponse.data.success) {
          setStoredNotary(tokenResponse?.data?.notary || null);
          setMemorableSelection(tokenResponse.data.memorableList);
          setPageLoading(false);
        } else {
          navigate("/login");
          throw new Error("Network response was not ok");
        }
        //}
      } catch (error) {
        console.error("Error fetching Email:", error);
        navigate("/login");
      }
    };
    if (count < 1) {
      count += 1;
      validate();
    }
  }, []);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const skipFormik = useFormik({
    enableReinitialize: false,
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must contain 8 or more characters")
        .max(255)
        .minLowercase(1, "Password must contain at least 1 lower case letter")
        .minUppercase(1, "Password must contain at least 1 upper case letter")
        .minNumbers(1, "Password must contain at least 1 number")
        .minSymbols(1, "Password must contain at least 1 special character")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .required("Password confirmation is required")
        .test("Passwords-match", "Passwords must match", function (value) {
          return this.parent.password === value;
        }),
    }),
    onSubmit: async (values) => {
      setSkipLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/finalize/skip-setup`,
          {
            payload: {
              firstName: firstNameQuery,
              lastName: lastNameQuery,
              token: tokenQuery,
              pwd: values.password,
              pwdConf: values.confirmPassword,
              timezone: userTimezone,
            },
          },
          { withCredentials: true },
        );
        if (response.data.success) {
          navigate("/notary/finalize");
        } else {
          setNotaryError(
            response.data.message || "Failed to complete registration",
          );
          setSkipLoading(false);
        }
      } catch (e) {
        setNotaryError(
          e?.response?.data?.message || "Failed to complete registration",
        );
        setSkipLoading(false);
      }
    },
  });

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: {
      password: "",
      confirmPassword: "",
      reminderQuestion: "",
      reminderAnswer: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must contain 8 or more characters")
        .max(255)
        .minLowercase(1, "Password must contain at least 1 lower case letter")
        .minUppercase(1, "Password must contain at least 1 upper case letter")
        .minNumbers(1, "Password must contain at least 1 number")
        .minSymbols(1, "Password must contain at least 1 special character")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .required("Password confirmation is required")
        .test("Passwords-match", "Passwords must match", function (value) {
          return this.parent.password === value;
        }),
      reminderQuestion: Yup.string().required("Security Question is required"),
      reminderAnswer: Yup.string()
        .required("Security question is required")
        .min(4, "Your answer must contain 4 or more characters"),
    }),
    onSubmit: async (values, helpers) => {
      setSubmitLoading(true);
      try {
        const payload = {
          firstName: firstNameQuery,
          lastName: lastNameQuery,
          token: tokenQuery,
          pwd: values["password"],
          pwdConf: values["confirmPassword"],
          memorable: values["reminderQuestion"],
          memorableText: values["reminderAnswer"],
          timezone: userTimezone,
        };

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/finalize-notary`,
          {
            payload,
          },
          { withCredentials: true },
        );

        if (response.data.success === true) {
          //! CREATE SESSION LOGIN FOR NOTARY
          navigate("/notary/finalize");
        } else if (response.data.success === false) {
          setNotaryError(response.data.message);
          setSubmitLoading(false);
        }
      } catch (e) {
        setNotaryError(e.response.data.message);
        setSubmitLoading(false);
      }
    },
  });

  return (
    <>
      {pageLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        </>
      ) : (
        <Box
          sx={
            isSmallScreen
              ? {
                  justifyContent: "center",
                  overflow: "auto",
                  width: "100%",
                  height: "100vh",
                }
              : {
                  backgroundImage: "url('/img/website_img/homeImg.jpeg')",
                  backgroundRepeat: "repeat",
                  width: "100%",
                  flex: "1 1 auto",
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  height: "100vh",
                }
          }
        >
          <Box
            sx={
              isSmallScreen
                ? {
                    display: "flex",
                    alignItems: "center",
                    flex: "1 1 auto",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                    py: "10px",
                    width: "100%",
                  }
                : {
                    overflowY: "auto",
                    backgroundColor: "#fff",
                    maxWidth: 900,
                    maxHeight: "95vh",
                    borderRadius: "25px",
                    px: 3,
                    py: "10px",
                    width: "100%",
                    pt: "2rem",
                  }
            }
          >
            <div
              style={
                isSmallScreen
                  ? { width: "100%", padding: "1rem" }
                  : { padding: "1rem" }
              }
            >
              <Stack spacing={1} sx={{ mb: 3 }}>
                <Box
                  sx={{ cursor: "pointer" }}
                  display="flex"
                  justifyContent="center"
                  onClick={() => {
                    navigate("/");
                  }}
                >
                  <img
                    style={{
                      width: "10rem",
                      marginLeft: "auto",
                      marginRight: "auto",
                      marginTop: "-9px",
                    }}
                    alt="Permit Rockstar Logo"
                    src="/img/logos/logo_main.png"
                  />
                </Box>
              </Stack>
              <Typography color="primary" variant="h4" align="center">
                Notary Registration
              </Typography>

              <Grid container justifyContent="center" spacing={2}>
                <>
                  <Grid container justifyContent="center" mb={2} mt={5}>
                    <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
                      <Typography
                        color="primary"
                        variant="body1"
                        align="center"
                        sx={{ mb: 5 }}
                      >
                        Welcome to Permit Rockstar, the Notary Platform. We
                        kindly request that you complete your Notary
                        registration by setting your password and security
                        details below.
                      </Typography>
                    </Grid>
                  </Grid>
                  {showChoiceScreen ? (
                    <>
                      <Box
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderRadius: "15px",
                          p: isSmallScreen ? 2 : 4,
                          mb: 3,
                          border: "2px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          align="center"
                          sx={{ mb: 2, fontWeight: 500 }}
                        >
                          We highly recommend enabling Two-Factor Authentication
                          (2FA) to protect your notary account
                        </Typography>
                        <List sx={{ mb: 2 }}>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText primary="✓ Adds an extra layer of security to your account" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText primary="✓ Protects access to your notary tools and jobs" />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText primary="✓ You can still enable or change it later in settings" />
                          </ListItem>
                        </List>
                      </Box>

                      {!showPasswordForSkip ? (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <LoadingButton
                              fullWidth
                              size="large"
                              variant="contained"
                              onClick={() => setShowChoiceScreen(false)}
                            >
                              Enable 2FA Now (Recommended)
                            </LoadingButton>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <LoadingButton
                              fullWidth
                              size="large"
                              variant="outlined"
                              color="warning"
                              onClick={() => setShowPasswordForSkip(true)}
                            >
                              Skip for Now
                            </LoadingButton>
                          </Grid>
                        </Grid>
                      ) : (
                        <form noValidate onSubmit={skipFormik.handleSubmit}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                              <Typography
                                align="center"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                Please create a password for your notary
                                account. You can enable 2FA later from your
                                settings.
                              </Typography>
                            </Grid>
                            <Grid size={{ sm: 12, md: 6 }}>
                              <TextField
                                error={
                                  !!(
                                    skipFormik.touched.password &&
                                    skipFormik.errors.password
                                  )
                                }
                                helperText={
                                  skipFormik.touched.password &&
                                  skipFormik.errors.password
                                }
                                type={showPassword ? "text" : "password"}
                                name="password"
                                label="Password"
                                variant="filled"
                                fullWidth
                                onBlur={skipFormik.handleBlur}
                                value={skipFormik.values.password}
                                onChange={skipFormik.handleChange}
                              />
                            </Grid>
                            <Grid size={{ sm: 12, md: 6 }}>
                              <TextField
                                error={
                                  !!(
                                    skipFormik.touched.confirmPassword &&
                                    skipFormik.errors.confirmPassword
                                  )
                                }
                                helperText={
                                  skipFormik.touched.confirmPassword &&
                                  skipFormik.errors.confirmPassword
                                }
                                type="password"
                                name="confirmPassword"
                                label="Confirm Password"
                                variant="filled"
                                fullWidth
                                onBlur={skipFormik.handleBlur}
                                value={skipFormik.values.confirmPassword}
                                onChange={skipFormik.handleChange}
                              />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <LoadingButton
                                type="submit"
                                loading={skipLoading}
                                size="large"
                                variant="contained"
                                fullWidth
                              >
                                Complete Registration Without 2FA
                              </LoadingButton>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Button
                                fullWidth
                                onClick={() => setShowPasswordForSkip(false)}
                              >
                                Back
                              </Button>
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </>
                  ) : (
                    <form noValidate onSubmit={formik.handleSubmit}>
                      <Grid container spacing={2}>
                        {!!storedNotary?.commissionNumber && (
                          <Grid size={{ xs: 12 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="center"
                            >
                              Commission #{storedNotary.commissionNumber}
                              {storedNotary?.commissionExpiry
                                ? ` • Expires ${storedNotary.commissionExpiry}`
                                : ""}
                            </Typography>
                          </Grid>
                        )}
                        <Grid size={{ sm: 12, md: 6 }}>
                          <TextField
                            error={
                              !!(
                                formik.touched.password &&
                                formik.errors.password
                              )
                            }
                            helperText={
                              formik.touched.password && formik.errors.password
                            }
                            type={showPassword ? "text" : "password"}
                            name="password"
                            label="Password"
                            variant="filled"
                            fullWidth
                            onBlur={formik.handleBlur}
                            value={formik.values.password}
                            onChange={(e) => {
                              formik.handleChange(e);
                            }}
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={handleTogglePasswordVisibility}
                                      onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                                      onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                                      edge="end"
                                    >
                                      {showPassword ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ sm: 12, md: 6 }}>
                          <TextField
                            error={
                              !!(
                                formik.touched.confirmPassword &&
                                formik.errors.confirmPassword
                              )
                            }
                            helperText={
                              formik.touched.confirmPassword &&
                              formik.errors.confirmPassword
                            }
                            type="password"
                            name="confirmPassword"
                            label="Confirm Password"
                            variant="filled"
                            fullWidth
                            onBlur={formik.handleBlur}
                            value={formik.values.confirmPassword}
                            onChange={(e) => {
                              formik.handleChange(e);
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography
                            align="center"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            Email 2FA is pre-selected for notary users. It can
                            be changed later in your account settings.
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <FormControl fullWidth variant="filled">
                            <InputLabel
                              sx={
                                formik.touched.reminderQuestion &&
                                Boolean(formik.errors.reminderQuestion)
                                  ? { color: "red" }
                                  : {}
                              }
                            >
                              Select a security question
                            </InputLabel>
                            <Select
                              error={
                                !!(
                                  formik.touched.reminderQuestion &&
                                  Boolean(formik.errors.reminderQuestion)
                                )
                              }
                              label="Select"
                              name="reminderQuestion"
                              onBlur={formik.handleBlur}
                              value={formik.values.reminderQuestion}
                              onChange={formik.handleChange}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    marginTop: "1.5px",
                                    maxHeight: 150, // Set the desired max height for the dropdown menu
                                    width: "auto",
                                    maxWidth: "100%",
                                  },
                                },
                              }}
                              sx={{
                                maxWidth: "100%", // Set the desired max-width for the input element
                                "& .MuiTypography-body2": {
                                  whiteSpace: "wrap", // Prevent text from wrapping or shrinking
                                },
                              }}
                            >
                              {memorableSelections?.map(function (item, i) {
                                return (
                                  <MenuItem key={i} value={item}>
                                    {item}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                            <FormHelperText sx={{ color: "red" }}>
                              {formik.touched.reminderQuestion &&
                                formik.errors.reminderQuestion}
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            error={
                              !!(
                                formik.touched.reminderAnswer &&
                                formik.errors.reminderAnswer
                              )
                            }
                            helperText={
                              formik.touched.reminderAnswer &&
                              formik.errors.reminderAnswer
                            }
                            fullWidth
                            name="reminderAnswer"
                            label="Please enter the answer to your security question"
                            type="text"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={formik.values.reminderAnswer}
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Typography
                            sx={{ minHeight: "1.38rem" }}
                            fontWeight="bold"
                            align="center"
                            variant="body2"
                            color="error"
                          >
                            {notaryError}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <LoadingButton
                            type="submit"
                            loading={submitLoading}
                            size="large"
                            variant="contained"
                            fullWidth
                          >
                            Submit
                          </LoadingButton>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Button
                            fullWidth
                            onClick={() => setShowChoiceScreen(true)}
                          >
                            Back
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  )}
                </>
              </Grid>
            </div>
          </Box>
        </Box>
      )}
    </>
  );
};

export default NotaryRegister;
